import { useState, useEffect, useCallback } from 'react';
import { generateTypedSignatureTemplates } from '../../components/documentSigning/utils';
import { DEFAULT_SIGNATURE_WIDTH_PCT } from '../../components/documentSigning/constants';

export const useSignaturePlacement = (defaultPlacement = null) => {
  const [signatureDraft, setSignatureDraft] = useState(null);
  const [signaturePosition, setSignaturePosition] = useState(null);
  const [signatureWidthPct, setSignatureWidthPct] = useState(DEFAULT_SIGNATURE_WIDTH_PCT);
  const [typedName, setTypedName] = useState('');
  const [typedTemplates, setTypedTemplates] = useState([]);
  const [typedSelectedTemplate, setTypedSelectedTemplate] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const trimmed = typedName.trim();
    if (!trimmed) {
      setTypedTemplates([]);
      setTypedSelectedTemplate(null);
      setSignatureDraft(null);
      return;
    }
    const templates = generateTypedSignatureTemplates(trimmed);
    setTypedTemplates(templates);
    setTypedSelectedTemplate((prev) => {
      if (prev && templates.some((tpl) => tpl.key === prev)) {
        return prev;
      }
      return templates[0]?.key || null;
    });
  }, [typedName]);

  useEffect(() => {
    const selected = typedTemplates.find((tpl) => tpl.key === typedSelectedTemplate) || null;
    setSignatureDraft(selected?.image || null);
  }, [typedTemplates, typedSelectedTemplate]);

  const buildSignatureImage = useCallback((silent = true, showToast = null) => {
    if (!typedSelectedTemplate) {
      if (!silent && showToast) {
        showToast('Select a typed signature style first.', 'error');
      }
      return null;
    }
    return signatureDraft;
  }, [typedSelectedTemplate, signatureDraft]);

  const hasSignatureImage = useCallback(() => {
    return Boolean(typedSelectedTemplate && signatureDraft);
  }, [typedSelectedTemplate, signatureDraft]);

  const resetSignaturePlacement = useCallback(() => {
    setSignaturePosition(null);
  }, []);

  const resetAll = useCallback(() => {
    setSignatureDraft(null);
    setSignaturePosition(null);
    setSignatureWidthPct(DEFAULT_SIGNATURE_WIDTH_PCT);
    setTypedName('');
    setTypedTemplates([]);
    setTypedSelectedTemplate(null);
  }, []);

  const setSignaturePositionHandler = useCallback((placement) => {
    setSignaturePosition(placement);
  }, []);

  return {
    signatureDraft,
    signaturePosition,
    signatureWidthPct,
    setSignatureWidthPct,
    typedName,
    setTypedName,
    typedTemplates,
    typedSelectedTemplate,
    setTypedSelectedTemplate,
    buildSignatureImage,
    hasSignatureImage,
    resetSignaturePlacement,
    resetAll,
    setSignaturePosition: setSignaturePositionHandler,
  };
};
